import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserApplications, withdrawApplication, APPLICATION_STATUS } from '../services/applicationService';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

import ContentWithAds from '../components/ContentWithAds';

const ApplicationHistory = ({ user, userData }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [withdrawing, setWithdrawing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userData?.userType !== 'jobseeker') {
      navigate('/dashboard');
      return;
    }

    fetchApplications();
  }, [user, userData, navigate]);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUserApplications(user.uid);
      if (result.success) {
        setApplications(result.applications);
        setError(null);
      } else {
        console.error('Failed to fetch applications:', result.error);
        setError(result.error);
        setApplications([]);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load applications. Please try again.');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      return;
    }

    setWithdrawing(applicationId);
    try {
      const result = await withdrawApplication(applicationId, user.uid);
      if (result.success) {
        // Remove the withdrawn application from the list
        setApplications(prev =>
          prev.filter(app => app.id !== applicationId)
        );
        alert('Application withdrawn successfully');
      } else {
        alert(result.error || 'Failed to withdraw application');
      }
    } catch (error) {
      console.error('Error withdrawing application:', error);
      alert('Failed to withdraw application');
    } finally {
      setWithdrawing(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [APPLICATION_STATUS.PENDING]: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      [APPLICATION_STATUS.REVIEWING]: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
      [APPLICATION_STATUS.SHORTLISTED]: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Shortlisted' },
      [APPLICATION_STATUS.INTERVIEWED]: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Interviewed' },
      [APPLICATION_STATUS.ACCEPTED]: { bg: 'bg-green-100', text: 'text-green-800', label: 'Accepted' },
      [APPLICATION_STATUS.REJECTED]: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      [APPLICATION_STATUS.WITHDRAWN]: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Withdrawn' }
    };

    const config = statusConfig[status] || statusConfig[APPLICATION_STATUS.PENDING];
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  const getStatusCounts = () => {
    const counts = {
      all: applications.length,
      pending: 0,
      reviewing: 0,
      shortlisted: 0,
      interviewed: 0,
      accepted: 0,
      rejected: 0,
      withdrawn: 0
    };

    applications.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pb-24 lg:pb-0">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">My Applications</h1>
              <p className="text-xl text-blue-100 mb-6">
                Track and manage your job applications
              </p>
              <div className="flex items-center justify-center gap-6 text-blue-100">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{applications.length}</div>
                  <div className="text-sm">Total Applications</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{statusCounts.pending + statusCounts.reviewing}</div>
                  <div className="text-sm">In Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{statusCounts.accepted}</div>
                  <div className="text-sm">Accepted</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ContentWithAds>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Filter Tabs */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'All Applications', count: statusCounts.all },
                  { key: APPLICATION_STATUS.PENDING, label: 'Pending', count: statusCounts.pending },
                  { key: APPLICATION_STATUS.REVIEWING, label: 'Under Review', count: statusCounts.reviewing },
                  { key: APPLICATION_STATUS.SHORTLISTED, label: 'Shortlisted', count: statusCounts.shortlisted },
                  { key: APPLICATION_STATUS.INTERVIEWED, label: 'Interviewed', count: statusCounts.interviewed },
                  { key: APPLICATION_STATUS.ACCEPTED, label: 'Accepted', count: statusCounts.accepted },
                  { key: APPLICATION_STATUS.REJECTED, label: 'Rejected', count: statusCounts.rejected }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${filter === tab.key
                      ? 'bg-indigo-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-red-900">Unable to Load Applications</h3>
                </div>
                <p className="text-red-700 mb-4">{error}</p>
                <button
                  onClick={fetchApplications}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Applications List */}
            {!error && filteredApplications.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {filter === 'all' ? 'No Applications Yet' : `No ${filter} Applications`}
                </h3>
                <p className="text-gray-600 mb-8">
                  {filter === 'all'
                    ? "You haven't applied to any jobs yet. Start exploring opportunities!"
                    : `You don't have any applications with ${filter} status.`
                  }
                </p>
                <Link
                  to="/jobs"
                  className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
                >
                  Browse Jobs
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            ) : !error && (
              <div className="space-y-6">
                {filteredApplications.map((application) => (
                  <div key={application.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden">
                    {/* Application Header */}
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-2">{application.jobTitle}</h3>
                          <p className="text-indigo-100 text-lg font-medium">{application.companyName}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-indigo-100">
                            <span>Applied: {new Date(application.appliedAt?.seconds * 1000 || application.appliedAt).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>Source: {application.applicationSource || 'Web Portal'}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          {getStatusBadge(application.status)}
                          <div className="flex items-center gap-3">
                            <Link
                              to={`/jobs/${application.jobId}`}
                              className="px-4 py-2 bg-white bg-opacity-20 text-white font-medium rounded-lg hover:bg-opacity-30 transition-colors"
                            >
                              View Job
                            </Link>
                            {[APPLICATION_STATUS.PENDING, APPLICATION_STATUS.REVIEWING].includes(application.status) && (
                              <button
                                onClick={() => handleWithdraw(application.id)}
                                disabled={withdrawing === application.id}
                                className="px-4 py-2 bg-red-500 bg-opacity-80 text-white font-medium rounded-lg hover:bg-opacity-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {withdrawing === application.id ? 'Withdrawing...' : 'Withdraw'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Application Details */}
                    <div className="p-6">
                      {/* Company Details */}
                      {application.companyDetails && (
                        <div className="mb-6 pb-6 border-b border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Company Details
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {application.companyDetails.email && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Email</span>
                                  <p className="text-gray-900">
                                    <a href={`mailto:${application.companyDetails.email}`} className="text-blue-600 hover:text-blue-800">
                                      {application.companyDetails.email}
                                    </a>
                                  </p>
                                </div>
                              )}
                              {application.companyDetails.phone && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Phone</span>
                                  <p className="text-gray-900">
                                    <a href={`tel:${application.companyDetails.phone}`} className="text-blue-600 hover:text-blue-800">
                                      {application.companyDetails.phone}
                                    </a>
                                  </p>
                                </div>
                              )}
                              {application.companyDetails.website && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Website</span>
                                  <p className="text-gray-900">
                                    <a
                                      href={application.companyDetails.website.startsWith('http') ? application.companyDetails.website : `https://${application.companyDetails.website}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 break-all"
                                    >
                                      {application.companyDetails.website}
                                    </a>
                                  </p>
                                </div>
                              )}
                              {(application.companyDetails.address || application.companyDetails.city || application.companyDetails.state || application.companyDetails.country) && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Address</span>
                                  <p className="text-gray-900">
                                    {[
                                      application.companyDetails.address,
                                      application.companyDetails.city,
                                      application.companyDetails.state,
                                      application.companyDetails.country,
                                      application.companyDetails.zipCode
                                    ].filter(Boolean).join(', ')}
                                  </p>
                                </div>
                              )}
                              {application.companyDetails.industry && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Industry</span>
                                  <p className="text-gray-900">{application.companyDetails.industry}</p>
                                </div>
                              )}
                              {application.companyDetails.companySize && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Company Size</span>
                                  <p className="text-gray-900">{application.companyDetails.companySize}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Personal Information */}
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Personal Information
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-sm font-medium text-gray-500">Name</span>
                                <p className="text-gray-900">{application.applicantName || 'Not provided'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-500">Email</span>
                                <p className="text-gray-900">{application.applicantEmail || 'Not provided'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-500">Phone</span>
                                <p className="text-gray-900">{application.applicantPhone || 'Not provided'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-500">Location</span>
                                <p className="text-gray-900">{application.applicantLocation || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Professional Summary */}
                          {application.applicantProfile?.professionalSummary && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2">Professional Summary</h5>
                              <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-gray-700 text-sm">{application.applicantProfile.professionalSummary}</p>
                              </div>
                            </div>
                          )}

                          {/* Skills */}
                          {application.applicantProfile?.skills && application.applicantProfile.skills.length > 0 && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2">Skills</h5>
                              <div className="flex flex-wrap gap-2">
                                {application.applicantProfile.skills.map((skill, index) => (
                                  <span key={index} className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Professional Details */}
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Professional Details
                          </h4>

                          {/* Experience */}
                          {application.applicantProfile?.experience && application.applicantProfile.experience.length > 0 && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2">Experience</h5>
                              <div className="space-y-3">
                                {application.applicantProfile.experience.map((exp, index) => (
                                  <div key={index} className="bg-green-50 rounded-lg p-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <h6 className="font-medium text-gray-900">{exp.jobTitle}</h6>
                                        <p className="text-sm text-gray-600">{exp.company} • {exp.location}</p>
                                      </div>
                                      <span className="text-xs text-gray-500">
                                        {exp.startDate} - {exp.isCurrentJob ? 'Present' : exp.endDate}
                                      </span>
                                    </div>
                                    {exp.description && (
                                      <p className="text-sm text-gray-700 mb-2">{exp.description}</p>
                                    )}
                                    {exp.achievements && (
                                      <p className="text-sm text-green-700"><strong>Achievements:</strong> {exp.achievements}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Education */}
                          {application.applicantProfile?.education && application.applicantProfile.education.length > 0 && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2">Education</h5>
                              <div className="space-y-3">
                                {application.applicantProfile.education.map((edu, index) => (
                                  <div key={index} className="bg-purple-50 rounded-lg p-3">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <h6 className="font-medium text-gray-900">{edu.degree} in {edu.fieldOfStudy}</h6>
                                        <p className="text-sm text-gray-600">{edu.institution} • {edu.location}</p>
                                        <p className="text-sm text-purple-700">Grade: {edu.grade} • Graduated: {edu.graduationYear}</p>
                                      </div>
                                    </div>
                                    {edu.description && (
                                      <p className="text-sm text-gray-700 mt-2">{edu.description}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Expected Salary */}
                          {application.applicantProfile?.expectedSalary && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2">Expected Salary</h5>
                              <div className="bg-yellow-50 rounded-lg p-3">
                                <p className="text-gray-900">
                                  {application.applicantProfile.expectedSalary.currency} {application.applicantProfile.expectedSalary.min} - {application.applicantProfile.expectedSalary.max}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Notice Period */}
                          {application.applicantProfile?.noticePeriod && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2">Notice Period</h5>
                              <div className="bg-orange-50 rounded-lg p-3">
                                <p className="text-gray-900">{application.applicantProfile.noticePeriod}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Information */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Cover Letter */}
                          {application.coverLetter && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2 flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Cover Letter
                              </h5>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-gray-700 text-sm whitespace-pre-wrap">{application.coverLetter}</p>
                              </div>
                            </div>
                          )}

                          {/* Resume */}
                          {application.resumeURL && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 mb-2 flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Resume
                              </h5>
                              <a
                                href={application.resumeURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Resume
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Application Timeline */}
                        <div className="mt-6">
                          <h5 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Application Timeline
                          </h5>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-gray-700">Applied</span>
                              </div>
                              <span className="text-gray-500">
                                {new Date(application.appliedAt?.seconds * 1000 || application.appliedAt).toLocaleString()}
                              </span>
                            </div>
                            {application.updatedAt && (
                              <div className="flex items-center justify-between text-sm mt-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span className="text-gray-700">Last Updated</span>
                                </div>
                                <span className="text-gray-500">
                                  {new Date(application.updatedAt?.seconds * 1000 || application.updatedAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {application.interviewScheduled && (
                              <div className="flex items-center justify-between text-sm mt-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                  <span className="text-gray-700">Interview Scheduled</span>
                                </div>
                                <span className="text-gray-500">
                                  {new Date(application.interviewScheduled?.seconds * 1000 || application.interviewScheduled).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Notes */}
                        {application.notes && application.notes.length > 0 && (
                          <div className="mt-4">
                            <h5 className="text-md font-medium text-gray-900 mb-2">Notes</h5>
                            <div className="bg-yellow-50 rounded-lg p-3">
                              {application.notes.map((note, index) => (
                                <p key={index} className="text-gray-700 text-sm mb-1">• {note}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ContentWithAds>
      </div>

      {/* Mobile Bottom Navigation */}


      {/* Footer */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default ApplicationHistory;
